import React, { useState, useCallback } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';

import { SmallModal } from '../../../components/UI/SmallModal/SmallModal';
import SaveNotification from '../../../components/UI/SaveNotification/SaveNotification';
import StudioProfile from "../../../components/StudioProfile/StudioProfile"
import NavBar from '../../../components/NavBar/NavBar';
import NavBarPhone from '../../../components/NavBarPhone/NavBarPhone';
import classes from "./Wishlist.module.css";
import { useFetchWishlist } from '../../../hooks/wishlists/useFetchWishlist';
import WishlistHeaderSkeleton from './WishlistHeaderSkeleton';

import { useDeleteWishlist } from '../../..//hooks/wishlists/useDeleteWishlist';
import { useUpdateWishlist } from '../../../hooks/wishlists/useUpdateWishlist';
import { shareUrl } from '../../../util/shareUtils';

export const Wishlist = () => {
  const [open, setOpen] = useState(false);
  const [isEditListOpen, setIsEditListOpen] = useState(false);
  const [newWishlistName, setNewWishlistName] = useState('');
  const [selectedWishlist, setSelectedWishlist] = useState(null);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [saveNotificationMessage, setSaveNotificationMessage] = useState('Änderungen gespeichert');

  const { id } = useParams();
  const navigate = useNavigate();

  const { wishlistData, loading, error } = useFetchWishlist(id);

  const { handleWishlistUpdate } = useUpdateWishlist();
  const { handleWishlistDeletion } = useDeleteWishlist();

  const showNotification = useCallback((message) => {
    setSaveNotificationMessage(message);
    setShowSaveNotification(true);
  }, []);

  const hideSaveNotification = useCallback(() => {
    setShowSaveNotification(false);
  }, []);

  const handleShare = () => shareUrl();
  
  const handleEdit = () => {
    if (wishlistData?.name === 'Favoriten') return; // Prevent editing Favoriten wishlist
    setSelectedWishlist(wishlistData);
    setNewWishlistName(wishlistData?.name || '');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setNewWishlistName('');
    setSelectedWishlist(null);
  };

  const handleEditListClick = () => {
    if (wishlistData?.name === 'Favoriten') return; // Prevent editing Favoriten wishlist
    setSelectedWishlist(wishlistData);
    setNewWishlistName(wishlistData?.name || '');
    setIsEditListOpen(true);
  };

  const handleEditListClose = () => {
    setIsEditListOpen(false);
    setNewWishlistName('');
    setSelectedWishlist(null);
  };

  const handleDeleteWishlist = async () => {
    try {
        if (!selectedWishlist || selectedWishlist.name === 'Favoriten') {
            return; // Prevent deleting Favoriten wishlist
        }

        await handleWishlistDeletion(selectedWishlist.id);
        handleClose();
        handleEditListClose();
        navigate("/wishlists", { state: { wishlistNotification: 'Kollektion gelöscht' } });
        
    } catch (error) {
        console.error('Error deleting wishlist:', error);
    }
  };

  const handleEditWishlist = async () => {
      try {
          if (!selectedWishlist || !newWishlistName?.trim() || selectedWishlist?.name === 'Favoriten') {
              return; // Prevent editing Favoriten wishlist
          }

          await handleWishlistUpdate({
              id: selectedWishlist?.id,
              name: newWishlistName
          });

          setSelectedWishlist(null);
          setNewWishlistName('');
          handleClose();
          handleEditListClose();
          showNotification('Änderungen gespeichert');
          
      } catch (error) {
          console.error('Error updating wishlist:', error);
      }
  };

  const backToWishlists = () => {
    navigate("/wishlists");
  };

  if (loading) {
    return (
      <div className={classes.wishlistContainer}>
        <div className={classes.wishlistNavBar}>
          <NavBar fromBookings={true} />
        </div>
        <header className={classes.phoneHeader}>
            <Link to="/">
                <svg xmlns="http://www.w3.org/2000/svg" width="53" height="18" viewBox="0 0 53 18" fill="none">
                    <path d="M50.5002 9C51.8809 9 53.0002 7.88071 53.0002 6.5C53.0002 5.11929 51.8809 4 50.5002 4C49.1195 4 48.0002 5.11929 48.0002 6.5C48.0002 7.88071 49.1195 9 50.5002 9Z" fill="#FF003E"/>
                    <path d="M9.93692 9.28445L8.58239 9.17069C5.23081 8.89942 4.10204 8.5494 4.10204 7.7881C4.10204 7.0268 5.52603 6.60677 8.46083 6.60677C11.5606 6.60677 12.8109 7.01805 13.0714 8.12062L13.1148 8.29563H16.5185L16.4838 8.05062C16.1452 5.14542 13.5577 3.73658 8.58239 3.73658C3.60712 3.73658 1.05437 5.11917 1.05437 7.83185C1.05437 10.317 3.09483 11.5158 8.11352 11.9709L9.32912 12.0846C12.6546 12.3734 13.8876 12.8109 13.8876 13.686C13.8876 14.6398 12.3681 15.1298 9.36385 15.1298C5.96017 15.1298 4.2236 14.491 4.05863 13.1697L4.03258 12.9772H0.628906V13.2135C0.785197 16.3899 3.63317 18 9.10336 18C14.5736 18 16.944 16.4949 16.944 13.5285C16.944 10.8858 14.9817 9.69572 9.93692 9.28445Z" fill="#1F1F1F"/>
                    <path d="M42.1415 4.17403V9.8444C42.1415 13.0209 40.0663 14.9897 36.7321 14.9897C33.5976 14.9897 32.3733 13.5809 32.3733 9.95816V4.17403H21.8497V0L18.7152 1.33009V13.2834C18.7152 16.5823 20.2954 17.9912 24.003 17.9912C25.2968 17.9912 26.5384 17.7724 27.589 17.5537L27.7627 17.5187V14.7097L27.5022 14.7622C26.139 15.0422 25.5312 15.1473 24.5587 15.1473C22.7614 15.1473 21.8497 14.7185 21.8497 12.3646V6.7992H30.793L29.1433 8.05928V10.982C29.1433 15.7685 31.1577 17.9912 35.4731 17.9912C38.2603 17.9912 40.5525 17.0199 42.1415 15.1648V17.5537H45.3715V4.17403H42.1415Z" fill="#1F1F1F"/>
                </svg>
            </Link>
        </header>
        <WishlistHeaderSkeleton />
        <div className={classes.navBarPhoneContainer}>
          <NavBarPhone fromFavourite={true}/>
        </div>
      </div>
    );
  }

  if (error) {
    return <div>Favoritenliste konnte nicht geladen werden.</div>;
  }

  if (!wishlistData) {
    return <div>Favoritenliste wurde nicht gefunden.</div>;
  }

  return (
    <div className={classes.wishlistContainer}>
      <div className={classes.wishlistNavBar}>
        <NavBar fromBookings={true} />
      </div>
        <header className={classes.phoneHeader}>
            <Link to="/">
                <svg xmlns="http://www.w3.org/2000/svg" width="53" height="18" viewBox="0 0 53 18" fill="none">
                    <path d="M50.5002 9C51.8809 9 53.0002 7.88071 53.0002 6.5C53.0002 5.11929 51.8809 4 50.5002 4C49.1195 4 48.0002 5.11929 48.0002 6.5C48.0002 7.88071 49.1195 9 50.5002 9Z" fill="#FF003E"/>
                    <path d="M9.93692 9.28445L8.58239 9.17069C5.23081 8.89942 4.10204 8.5494 4.10204 7.7881C4.10204 7.0268 5.52603 6.60677 8.46083 6.60677C11.5606 6.60677 12.8109 7.01805 13.0714 8.12062L13.1148 8.29563H16.5185L16.4838 8.05062C16.1452 5.14542 13.5577 3.73658 8.58239 3.73658C3.60712 3.73658 1.05437 5.11917 1.05437 7.83185C1.05437 10.317 3.09483 11.5158 8.11352 11.9709L9.32912 12.0846C12.6546 12.3734 13.8876 12.8109 13.8876 13.686C13.8876 14.6398 12.3681 15.1298 9.36385 15.1298C5.96017 15.1298 4.2236 14.491 4.05863 13.1697L4.03258 12.9772H0.628906V13.2135C0.785197 16.3899 3.63317 18 9.10336 18C14.5736 18 16.944 16.4949 16.944 13.5285C16.944 10.8858 14.9817 9.69572 9.93692 9.28445Z" fill="#1F1F1F"/>
                    <path d="M42.1415 4.17403V9.8444C42.1415 13.0209 40.0663 14.9897 36.7321 14.9897C33.5976 14.9897 32.3733 13.5809 32.3733 9.95816V4.17403H21.8497V0L18.7152 1.33009V13.2834C18.7152 16.5823 20.2954 17.9912 24.003 17.9912C25.2968 17.9912 26.5384 17.7724 27.589 17.5537L27.7627 17.5187V14.7097L27.5022 14.7622C26.139 15.0422 25.5312 15.1473 24.5587 15.1473C22.7614 15.1473 21.8497 14.7185 21.8497 12.3646V6.7992H30.793L29.1433 8.05928V10.982C29.1433 15.7685 31.1577 17.9912 35.4731 17.9912C38.2603 17.9912 40.5525 17.0199 42.1415 15.1648V17.5537H45.3715V4.17403H42.1415Z" fill="#1F1F1F"/>
                </svg>
            </Link>
        </header>
        <button onClick={backToWishlists} className={`${classes.buttons} ${classes.backButton}`}>
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="18" viewBox="0 0 10 18" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M0.636719 8.67179L8.53898 0.769531L9.59461 1.82516L2.70721 8.71256L9.63672 16.2195L8.53974 17.2321L0.651904 8.68697L0.636719 8.67179Z" fill="#1F1F1F"/>
          </svg>
        </button>
        <div className={classes.wishlistHeader}>
          <div className={classes.wishlistHeaderUp}>
            <div onClick={backToWishlists} className={classes.wishlistHeaderUpLeft}>
              <button className={`${classes.buttons} ${classes.backButton2}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="18" viewBox="0 0 10 18" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M0.636719 8.67179L8.53898 0.769531L9.59461 1.82516L2.70721 8.71256L9.63672 16.2195L8.53974 17.2321L0.651904 8.68697L0.636719 8.67179Z" fill="#1F1F1F"/>
                </svg>
              </button>
              <h1 className='titleSemibold2'>{wishlistData?.name}</h1>
            </div>
            <div className={classes.headerButtonsContainer}>
              <button 
                onClick={handleEdit} 
                className={`${classes.buttons} ${classes.editButton} ${wishlistData?.name === 'Favoriten' ? classes.hidden : ''}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="33" height="32" viewBox="0 0 33 32" fill="none">
                  <circle cx="16.668" cy="16" r="16" fill="#F0F1F4"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M19.4357 9.21713C19.6544 8.99841 19.9859 8.9385 20.2674 9.06684C20.8753 9.34403 21.5299 9.83094 22.0995 10.3609C22.6722 10.8937 23.2105 11.5182 23.5632 12.1092C23.7372 12.4007 23.6909 12.7731 23.4508 13.0132L21.6609 14.8023C21.6404 14.8236 21.6197 14.8442 21.5988 14.8643L20.092 16.3704C19.7636 16.6989 19.4351 17.0275 19.1065 17.3561C17.3977 19.0653 15.6874 20.776 13.9813 22.4809C13.8535 22.6087 13.6825 22.6862 13.5021 22.6973L10.4549 22.8854C10.2427 22.8985 10.0352 22.8199 9.88491 22.6695C9.73466 22.5192 9.65618 22.3116 9.66941 22.0994L9.85605 19.106C9.8543 18.9141 9.92666 18.7216 10.0731 18.5751L11.2727 17.3756C11.2901 17.3581 11.3082 17.3417 11.3269 17.3264L17.6383 11.015C17.6431 11.0098 17.6481 11.0048 17.653 10.9998C18.141 10.5105 18.5881 10.0639 18.9701 9.68235C19.1387 9.51396 19.2946 9.35824 19.4357 9.21713ZM12.2621 18.4828L11.3217 19.4232L11.2013 21.354L13.1307 21.2349L18.6502 15.7155C18.65 15.7156 18.6503 15.7153 18.6502 15.7155L19.1791 15.1866C18.8018 15.0238 18.4543 14.7861 18.1622 14.5157C17.8316 14.2097 17.5375 13.8296 17.3433 13.4078L16.735 14.0167C14.6154 16.1345 14.4435 16.3064 12.3257 18.426C12.3054 18.4463 12.2842 18.4652 12.2621 18.4828ZM18.7109 12.0389C18.5909 12.1752 18.5522 12.3388 18.6127 12.5742C18.6813 12.8412 18.8759 13.1562 19.1694 13.4279C19.4629 13.6996 19.7918 13.8693 20.0631 13.9171C20.2828 13.9558 20.436 13.9157 20.5578 13.8079C20.5781 13.7875 20.5984 13.7672 20.6188 13.7469C20.6194 13.7463 20.62 13.7457 20.6206 13.745C20.6281 13.7376 20.6357 13.7303 20.6434 13.7232L21.9672 12.4C21.7246 12.0855 21.4216 11.7551 21.0896 11.4463C20.7508 11.131 20.4048 10.8596 20.0904 10.6587C20.0661 10.683 20.0415 10.7076 20.0166 10.7324C19.6398 11.1088 19.2009 11.5472 18.7211 12.0283C18.7177 12.0319 18.7143 12.0354 18.7109 12.0389Z" fill="black"/>
                </svg>
              </button>
              <button 
                onClick={handleEditListClick} 
                className={`${classes.buttons} ${classes.editButtonPhone} ${wishlistData?.name === 'Favoriten' ? classes.hidden : ''}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="33" height="32" viewBox="0 0 33 32" fill="none">
                  <circle cx="16.668" cy="16" r="16" fill="#F0F1F4"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M19.4357 9.21713C19.6544 8.99841 19.9859 8.9385 20.2674 9.06684C20.8753 9.34403 21.5299 9.83094 22.0995 10.3609C22.6722 10.8937 23.2105 11.5182 23.5632 12.1092C23.7372 12.4007 23.6909 12.7731 23.4508 13.0132L21.6609 14.8023C21.6404 14.8236 21.6197 14.8442 21.5988 14.8643L20.092 16.3704C19.7636 16.6989 19.4351 17.0275 19.1065 17.3561C17.3977 19.0653 15.6874 20.776 13.9813 22.4809C13.8535 22.6087 13.6825 22.6862 13.5021 22.6973L10.4549 22.8854C10.2427 22.8985 10.0352 22.8199 9.88491 22.6695C9.73466 22.5192 9.65618 22.3116 9.66941 22.0994L9.85605 19.106C9.8543 18.9141 9.92666 18.7216 10.0731 18.5751L11.2727 17.3756C11.2901 17.3581 11.3082 17.3417 11.3269 17.3264L17.6383 11.015C17.6431 11.0098 17.6481 11.0048 17.653 10.9998C18.141 10.5105 18.5881 10.0639 18.9701 9.68235C19.1387 9.51396 19.2946 9.35824 19.4357 9.21713ZM12.2621 18.4828L11.3217 19.4232L11.2013 21.354L13.1307 21.2349L18.6502 15.7155C18.65 15.7156 18.6503 15.7153 18.6502 15.7155L19.1791 15.1866C18.8018 15.0238 18.4543 14.7861 18.1622 14.5157C17.8316 14.2097 17.5375 13.8296 17.3433 13.4078L16.735 14.0167C14.6154 16.1345 14.4435 16.3064 12.3257 18.426C12.3054 18.4463 12.2842 18.4652 12.2621 18.4828ZM18.7109 12.0389C18.5909 12.1752 18.5522 12.3388 18.6127 12.5742C18.6813 12.8412 18.8759 13.1562 19.1694 13.4279C19.4629 13.6996 19.7918 13.8693 20.0631 13.9171C20.2828 13.9558 20.436 13.9157 20.5578 13.8079C20.5781 13.7875 20.5984 13.7672 20.6188 13.7469C20.6194 13.7463 20.62 13.7457 20.6206 13.745C20.6281 13.7376 20.6357 13.7303 20.6434 13.7232L21.9672 12.4C21.7246 12.0855 21.4216 11.7551 21.0896 11.4463C20.7508 11.131 20.4048 10.8596 20.0904 10.6587C20.0661 10.683 20.0415 10.7076 20.0166 10.7324C19.6398 11.1088 19.2009 11.5472 18.7211 12.0283C18.7177 12.0319 18.7143 12.0354 18.7109 12.0389Z" fill="black"/>
                </svg>
              </button>
              <button onClick={handleShare} className={`${classes.buttons} ${classes.shareButton}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="33" height="32" viewBox="0 0 33 32" fill="none">
                  <circle cx="16.668" cy="16" r="16" fill="#F0F1F4"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M16.6748 6.93164L19.7392 10.003L18.9059 10.8241L17.2547 9.16917V17.3092H16.0811V9.17957L14.4293 10.8311L13.597 10.009L16.6748 6.93164ZM12.6632 13.1813C12.2131 13.1813 11.8415 13.5465 11.8415 14.0098V21.0748C11.8415 21.538 12.2131 21.9033 12.6632 21.9033H20.6727C21.1229 21.9033 21.4945 21.538 21.4945 21.0748V14.0098C21.4945 13.5465 21.1229 13.1813 20.6727 13.1813H19.5019V12.015H20.6727C21.7804 12.015 22.668 12.9118 22.668 14.0098V21.0748C22.668 22.1728 21.7804 23.0696 20.6727 23.0696H12.6632C11.5556 23.0696 10.668 22.1728 10.668 21.0748V14.0098C10.668 12.9118 11.5556 12.015 12.6632 12.015H13.8911V13.1813H12.6632Z" fill="black" stroke="black" strokeWidth="0.2" strokeMiterlimit="10"/>
                </svg>
              </button>
            </div>
          </div>
          <div className={classes.wishlistHeaderDown}>
            <span className="regularLarge">{wishlistData?.wishListStudios?.items?.length || 0} Studios</span>
          </div>
        </div>
        <div className={classes.longHorizontalLine}></div>
        {wishlistData?.wishListStudios?.items?.filter(item => item?.studio).length === 0 ? (
          <div className={classes.emptyStateContainer}>
            <div className={classes.emptyStateBoxesContainer}>
              <div className={classes.emptyStateBox}></div>
              <div className={classes.emptyStateBox}></div>
              <div className={classes.emptyStateBox}></div>
            </div>
            <div className={classes.emptyStateContent}>
              <h2 className={classes.emptyStateTitle}>Noch keine Favoriten</h2>
              <p className={`${classes.emptyStateText} mediumLarge`}>
                Deine Favoriten einfach merken und<br />später wiederfinden!
              </p>
              <Link to="/search" className={classes.discoverButton}>
                Studios entdecken
              </Link>
            </div>
          </div>
        ) : (
          <div className={classes.studiosFoldersContainer}>
            {(wishlistData?.wishListStudios?.items
                ?.filter(item => item?.studio)
                ?.map(item => item.studio) || []).map((studio) => (
              <StudioProfile 
                key={studio?.id}
                studio={studio}
                renderedBy="Wishlist"
                fromSearch
                fromWishlist
              />
            ))}
          </div>
        )}
        <div className={classes.navBarPhoneContainer}>
            <NavBarPhone fromFavourite={true}/>
        </div>
        <SmallModal fromEditWishlist={true} open={open} onBackdropClick={handleClose} className={classes.smallModal}>
            <div className={classes.modalContent}>
                <h2 className='titleSemibold3'>Kollektion bearbeiten</h2>
                <div onClick={handleClose} className={classes.closeButton}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                    </svg>
                </div>
                <div className={classes.modalInputContainer}>
                    <h3 className='semiboldSmall'>Name</h3>
                    <input 
                        style={{margin: '0'}} 
                        type="text" 
                        placeholder='Name eingeben' 
                        className={`${classes.input} regularLarge`}
                        value={newWishlistName}
                        onChange={(e) => setNewWishlistName(e.target.value)}
                    />
                </div>
                <button 
                    onClick={handleDeleteWishlist} 
                    className={`${classes.deleteListButton} mediumLarge ${wishlistData?.name === 'Favoriten' ? classes.hidden : ''}`}
                >
                    Kollektion löschen
                </button>
                <div className={classes.modalButtons}>
                    <button 
                        className={`${classes.modalButton} mediumLarge`} 
                        onClick={handleClose}
                        style={{border: '1px solid var(--light-grey-1)', color: 'var(--dark-grey)'}}
                    >
                        Abbrechen
                    </button>
                    <button 
                        className={`${classes.modalButton2} mediumLarge`} 
                        onClick={handleEditWishlist}
                        style={{color: 'var(--white)', border: 'none', padding: '13px 21px'}}
                    >
                        Speichern
                    </button>
                </div>
            </div>
        </SmallModal>
        <div 
            className={`${classes.overlay} ${isEditListOpen ? classes.overlayActive : ''}`}
            onClick={handleEditListClose}
        />
        <div className={`${classes.newListPhoneContainer} ${isEditListOpen ? classes.newListPhoneContainerActive : ''}`}>
            <div onClick={handleEditListClose} className={classes.closeButton}>
                <svg className={classes.closeIcon} xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                </svg>
            </div>
            <h2 className='titleSemibold3'>Kollektion bearbeiten</h2>
            <div className={classes.horizontalLine}></div>
            <div className={classes.newListPhoneInputContainer}>
                <button 
                    style={{marginBottom: '0px'}} 
                    onClick={handleDeleteWishlist} 
                    className={`${classes.deleteListButton} mediumLarge ${wishlistData?.name === 'Favoriten' ? classes.hidden : ''}`}
                >
                    Kollektion löschen
                </button>
                <h3 className='semiboldSmall'>Name</h3>
                <input 
                    type="text" 
                    placeholder='Name eingeben' 
                    className={`${classes.input} regularLarge`}
                    value={newWishlistName}
                    onChange={(e) => setNewWishlistName(e.target.value)}
                />
            </div>
            <div className={classes.modalButtons}>
                <button 
                    className={`${classes.modalButton} mediumLarge`} 
                    onClick={handleEditListClose}
                    style={{border: '1px solid var(--light-grey-1)', color: 'var(--dark-grey)'}}
                >
                    Abbrechen
                </button>
                <button 
                    className={`${classes.modalButton2} mediumLarge`} 
                    onClick={handleEditWishlist}
                    style={{color: 'var(--white)', border: 'none', padding: '13px 21px'}}
                >
                    Speichern
                </button>
            </div>
        </div>
        <SaveNotification
            show={showSaveNotification}
            onHide={hideSaveNotification}
            message={saveNotificationMessage}
        />
    </div>
  );
};

export default Wishlist;
